import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import Table from '../../../../components/molecules/Table'
import Edit from '../ExternalChannelsColumns/Edit'
import AssociatedUser from '../ExternalChannelsColumns/AssociatedUser'
import SettingsSection from '../../SettingsSection'
import PhoneFlag from '../../../../components/atoms/PhoneFlag'
import ExternalChannelsModal from '../../../../components/molecules/ChatModals/ExternalChannelsModal'
import NoPermissionContainer from '../../../../components/molecules/NoPermissionContainer'
import ErrorToast from '../../../../components/molecules/ErrorToast'

import { columnHelper } from './helpers'
import {
  fetchExternalChannels,
  selectExternalChannel
} from '../../../../slices/externalChannel'
import { fetchInboxes } from '../../../../slices/inboxes'
import { useBreakpoint } from '../../../../providers/BreakpointProvider'
import { getFormatted } from '../../../../utilities/phoneNumber'

const ExternalChannelsTable = () => {
  const dispatch = useAppDispatch()
  const { loaded, loading, channels, error } = useAppSelector(
    selectExternalChannel
  )
  const {
    loaded: inboxesLoaded,
    loading: inboxesLoading,
    error: inboxesError,
    inboxes
  } = useAppSelector(storeState => storeState.inboxes)
  const { isExtraSmall, isSmall, isMedium } = useBreakpoint()
  const [showModal, setShowModal] = React.useState(false)
  const [data, setData] = React.useState(null)

  // Opens the modal
  const openModal = React.useCallback(
    data => () => {
      if (data) {
        setData(data)
      }
      setShowModal(true)
    },
    []
  )
  // Closes the modal
  const handleClose = React.useCallback(() => {
    setShowModal(false)
    setData(null)
  }, [])

  React.useEffect(() => {
    if (!inboxesLoaded && !inboxesLoading) {
      dispatch(fetchInboxes())
    }
  }, [inboxesLoaded, inboxesLoading, dispatch])

  React.useEffect(() => {
    if (!loading && !loaded) {
      dispatch(fetchExternalChannels())
    }
  }, [loaded, loading, dispatch])

  const getInboxValue = React.useCallback(
    value => {
      return inboxes[value]?.name
    },
    [inboxes]
  )

  const columns = React.useMemo(() => {
    const typeColumn = columnHelper.accessor('type', {
      header: 'Type',
      cell: ({ row }) => (
        <div style={{ cursor: 'default' }}>{row.original.type}</div>
      )
    })

    const valueColumn = columnHelper.accessor('value', {
      header: 'Value',
      cell: ({ row }) => {
        if (row.original.type === 'email') {
          return <div style={{ cursor: 'default' }}>{row.original.value}</div>
        }

        const formatted = getFormatted(row.original.value)
        return (
          <div style={{ cursor: 'default' }}>
            <PhoneFlag style={{ marginRight: '5px' }} value={formatted} />
            {formatted}
          </div>
        )
      }
    })

    const defaultInboxColumn = columnHelper.accessor('default_inbox_id', {
      header: 'Default Inbox',
      cell: ({ row }) => {
        if (row.original.default_inbox_id) {
          return (
            <div style={{ cursor: 'default' }}>
              {getInboxValue(row.original.default_inbox_id)}
            </div>
          )
        }

        return (
          <div style={{ color: 'lightgrey', cursor: 'default' }}>No inbox</div>
        )
      }
    })

    const associatedUserColumn = columnHelper.accessor('associated_user_id', {
      header: 'Associated User',
      cell: ({ row }) => (
        <AssociatedUser userId={row.original.associated_user_id} />
      )
    })

    const editColumn = columnHelper.display({
      id: 'edit',
      header: 'Edit',
      cell: ({ row }) => <Edit open={openModal} data={row.original} />
    })

    if (isExtraSmall || isSmall) {
      return [typeColumn, editColumn]
    } else if (isMedium) {
      return [typeColumn, valueColumn, editColumn]
    }
    return [
      typeColumn,
      valueColumn,
      defaultInboxColumn,
      associatedUserColumn,
      editColumn
    ]
  }, [openModal, isExtraSmall, isSmall, isMedium, getInboxValue])

  return (
    <NoPermissionContainer skipAgentPage error={error || inboxesError}>
      <SettingsSection title='Settings' loaded={loaded && inboxesLoaded}>
        <Table
          id='external-channels'
          data={channels}
          columns={columns}
          loaded={loaded && inboxesLoaded}
          message='You have no external channels'
          hideCount
          disablePagination
        />

        {showModal && (
          <ExternalChannelsModal
            open={showModal}
            onClose={handleClose}
            data={data}
          />
        )}
      </SettingsSection>
      <ErrorToast error={error || inboxesError} />
    </NoPermissionContainer>
  )
}

export default ExternalChannelsTable
